/* eslint-disable import/first */
if (process.env.NODE_ENV === 'development') {
  // Must be the first import
  import('preact/debug');
}

import './main.css';
import './product-card/product-card.css';
import Layout from './utils/layout';
import Video from './utils/video';
import SPDButton from './spd-button';
import initFocusMode from './utils/focus-mode';

initFocusMode();

(() => {
  const layout = new Layout();
  const moduleList = {};
  const modulePromises = {};
  const initializedSections = new WeakSet();
  const importedSections = new WeakSet();

  const getSections = (root) => {
    const sections = root.querySelectorAll
      ? [...root.querySelectorAll('[data-section]')]
      : [];

    if (root.matches?.('[data-section]')) sections.unshift(root);

    return sections;
  };

  // Gating is opt-in per page: layout/theme.liquid sets data-defer-sections on
  // <body> for the templates where it has been validated. Everywhere else every
  // section loads eagerly, exactly as before.
  const deferSections = document.body?.hasAttribute('data-defer-sections');
  const LOAD_STRATEGIES = ['eager', 'visible', 'idle', 'manual'];

  let visibilityObserver = null;

  const strategyFor = (element) => {
    if (!deferSections) return 'eager';

    const requested = element.dataset.sectionLoad;
    return LOAD_STRATEGIES.includes(requested) ? requested : 'eager';
  };

  const importSection = (element) => {
    const { section: name, sectionId } = element.dataset;

    // Separate from initializedSections, which guards *scheduling*. A manual
    // load bypasses the scheduler, so the actual import needs its own guard or
    // the section's module would run - and mount - twice.
    if (importedSections.has(element)) return modulePromises[name];

    importedSections.add(element);

    modulePromises[name] ??= import(
      /* webpackChunkName: "[index]" */ `./${name}/index.js`
    );

    // Returned to callers instead of the raw import promise: a section whose
    // module fails to evaluate must not reject into window.themeSections.load(),
    // which is fire-and-forget.
    const settled = modulePromises[name]
      .then((m) => {
        const module = m.default;
        const dataEl = element.querySelector('[data-section-data]');
        let data = {};

        try {
          data = dataEl ? JSON.parse(dataEl.textContent) : {};
        } catch (e) {
          console.error('Error parsing data for section:', e, dataEl);
        }

        const videoEls = element.querySelectorAll('[data-component="video"]');
        const videos = [...videoEls].map(
          (videoEl) => new Video({ element, video: videoEl, layout }),
        );

        if (sectionId) {
          moduleList[sectionId] = {
            m,
            data,
            videos,
          };
        }

        module(element, { ...data, layout, videos });
      })
      .catch((error) => {
        initializedSections.delete(element);
        // Drop the rejected promise too, otherwise every later element with this
        // name re-attaches to the same rejection and re-logs it forever.
        delete modulePromises[name];
        // eslint-disable-next-line no-console
        console.error(error);
      });

    return settled;
  };

  const scheduleIdle = (element) => {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(() => importSection(element), {
        timeout: 2000,
      });
      return;
    }

    globalThis.setTimeout(() => importSection(element), 1);
  };

  const getVisibilityObserver = () => {
    if (visibilityObserver || !('IntersectionObserver' in window)) {
      return visibilityObserver;
    }

    visibilityObserver = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            observer.unobserve(entry.target);
            importSection(entry.target);
            return;
          }

          // Several sections render an empty shell and only get a box once their
          // module has run. A zero-area element can never intersect, so without
          // this it would stay blank forever - fall back to idle instead.
          const { width, height } = entry.boundingClientRect;

          if (width === 0 && height === 0) {
            observer.unobserve(entry.target);
            scheduleIdle(entry.target);
          }
        });
      },
      { rootMargin: '200px 0px' },
    );

    return visibilityObserver;
  };

  const scheduleVisible = (element) => {
    const observer = getVisibilityObserver();

    if (!observer) {
      importSection(element);
      return;
    }

    observer.observe(element);
  };

  const loadSection = (element) => {
    const { section: name } = element.dataset;

    if (!name || initializedSections.has(element)) return;

    initializedSections.add(element);

    switch (strategyFor(element)) {
      case 'visible':
        scheduleVisible(element);
        break;
      case 'idle':
        scheduleIdle(element);
        break;
      case 'manual':
        // Nothing to do: window.themeSections.load(name) is the trigger.
        break;
      default:
        importSection(element);
    }
  };

  const loadSections = (root = document) => {
    getSections(root).forEach(loadSection);
  };

  /**
   * Loads a section on demand, for markup that opts out of automatic loading
   * with data-section-load="manual". main.js is deferred, so callers that run
   * at parse time queue their request and it is drained here.
   */
  const loadSectionByName = (name) =>
    Promise.all(
      getSections(document)
        .filter((element) => element.dataset.section === name)
        .map(importSection),
    );

  const queued = window.themeSections?.queue ?? [];
  window.themeSections = { load: loadSectionByName, queue: [] };
  queued.forEach((name) => loadSectionByName(name));

  loadSections();

  if (window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', (e) => {
      loadSections(e.target);
    });
  }

  const sectionObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) loadSections(node);
      });
    });
  });

  sectionObserver.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  const openPanel = (e, target, bodyEl) => {
    if (!target) return;
    const targetEl = target.dataset.container;
    const container = document.querySelector(`[data-${targetEl}]`);
    const chatLauncher = document
      .getElementById('launcher')
      ?.parentElement?.closest('div');
    bodyEl.classList.add('disable-scroll');
    container.classList.add('panel-active');
    chatLauncher.style.display = 'none';
  };

  const closePanel = (el, bodyEl) => {
    const target = document.querySelectorAll('[data-panel]');
    const chatLauncher = document
      .getElementById('launcher')
      ?.parentElement?.closest('div');
    if (!target.length) return;
    target.forEach((el) => {
      el.classList.remove('panel-active');
    });
    bodyEl.classList.remove('disable-scroll');
    chatLauncher.style.display = 'block';
  };

  const panelInit = () => {
    const bodyEl = document.querySelector('body');
    document.querySelectorAll('[data-panel-open]').forEach((el) => {
      el.addEventListener('click', (e) => {
        const target = el;
        openPanel(e, target, bodyEl);
      });
    });

    const closingPanelEls = document.querySelectorAll('[data-panel-close]');

    document
      .querySelector('.backdrop-overlay')
      ?.addEventListener('click', (e) => {
        closePanel(e.target, bodyEl);
      });

    if (!closingPanelEls.length) return;

    closingPanelEls.forEach((el) => {
      el.addEventListener('click', () => {
        closePanel(el, bodyEl);
      });
    });
  };

  panelInit();

  // Initialize all SPD button components on page load
  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-component="spd-button"]').forEach((el) => {
      const dataEl = el.querySelector('[data-section-data]');
      if (dataEl) {
        try {
          const data = JSON.parse(dataEl.textContent);
          SPDButton(el, data);
        } catch (e) {
          console.error('Error initializing SPD Button component:', e);
        }
      }
    });
  });
})();
